<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <button class="header-btn" @click="close">취소</button>
        <h2>{{ isEdit ? '질문 수정' : '질문 작성' }}</h2>
        <button 
          class="header-btn primary" 
          @click="submit" 
          :disabled="!isValid"
        >
          {{ isEdit ? '완료' : '등록' }}
        </button>
      </div>

      <!-- Form -->
      <div class="form-content">
        <!-- 카테고리 -->
        <div class="form-group">
          <label>카테고리</label>
          <div class="category-select">
            <button 
              v-for="cat in categories" 
              :key="cat"
              :class="['cat-option', { active: form.category === cat }]"
              @click="form.category = cat"
            >
              {{ cat }}
            </button>
          </div>
        </div>

        <!-- 제목 -->
        <div class="form-group">
          <label>제목 <span class="required">*</span></label>
          <input 
            type="text" 
            v-model="form.title" 
            placeholder="질문 제목을 입력하세요"
          />
        </div>

        <!-- 내용 -->
        <div class="form-group">
          <label>내용 <span class="required">*</span></label>
          <textarea 
            v-model="form.content" 
            placeholder="질문 내용을 자세히 작성해주세요"
            rows="6"
          ></textarea>
        </div>

        <!-- 이미지 첨부 -->
        <div class="form-group">
          <label>이미지 첨부</label>
          
          <!-- 이미지 미리보기 -->
          <div class="image-preview-list" v-if="form.images.length > 0">
            <div 
              class="image-item" 
              v-for="(img, idx) in form.images" 
              :key="idx"
              draggable="true"
              @dragstart="onDragStart($event, idx)"
              @dragend="onDragEnd"
              @dragover.prevent="onDragOver($event, idx)"
              @drop.prevent="onDrop(idx)"
              :class="{ dragging: dragIndex === idx, dragover: dragOverIndex === idx }"
            >
              <img :src="img" alt="미리보기" @click="openViewer(idx)" />
              <button class="remove-btn" @click="removeImage(idx)">
                <i class="ph ph-x"></i>
              </button>
              <span class="img-number">{{ idx + 1 }}</span>
            </div>
            
            <!-- 이미지 추가 버튼 -->
            <button class="add-image-btn" @click="triggerFileInput" :disabled="uploading">
              <i class="ph" :class="uploading ? 'ph-spinner spin' : 'ph-plus'"></i>
            </button>
          </div>
          
          <!-- 빈 상태 업로드 -->
          <div class="image-upload-empty" v-else @click="triggerFileInput">
            <i class="ph" :class="uploading ? 'ph-spinner spin' : 'ph-image'"></i>
            <span v-if="!uploading">클릭하여 이미지 추가</span>
            <span v-else>업로드 중...</span>
          </div>

          <input 
            type="file" 
            ref="fileInput"
            accept="image/*"
            multiple
            @change="handleFileSelect"
            style="display: none;"
          />
        </div>

        <!-- 비공개 설정 -->
        <div class="form-group">
          <label class="checkbox-label" @click="form.is_secret = !form.is_secret">
            <span class="checkbox" :class="{ checked: form.is_secret }">
              <i class="ph ph-check" v-if="form.is_secret"></i>
            </span>
            <span>비공개 질문</span>
          </label>
          <p class="hint">비공개 질문은 작성자와 답변자만 볼 수 있습니다</p>
        </div>
      </div>

      <!-- Image Viewer -->
      <div class="image-viewer" v-if="showViewer" @click="showViewer = false">
        <button class="viewer-close">
          <i class="ph ph-x"></i>
        </button>
        <div class="viewer-content" @click.stop>
          <img :src="form.images[viewerIndex]" alt="이미지" />
        </div>
        <button 
          class="viewer-arrow prev" 
          v-if="form.images.length > 1 && viewerIndex > 0"
          @click.stop="viewerIndex--"
        >
          <i class="ph ph-caret-left"></i>
        </button>
        <button 
          class="viewer-arrow next" 
          v-if="form.images.length > 1 && viewerIndex < form.images.length - 1"
          @click.stop="viewerIndex++"
        >
          <i class="ph ph-caret-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, onMounted } from "vue"

export default {
  props: {
    oth_showQnaEdit: Function,
    editIdx: { default: null }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const categories = ['자동차보험', '실손보험', '생명보험', '연금/저축', '화재/재물', '기타']
    
    const fileInput = ref(null)
    const uploading = ref(false)
    const isEdit = computed(() => props.editIdx !== null)
    
    // 이미지 뷰어
    const showViewer = ref(false)
    const viewerIndex = ref(0)
    
    // 드래그
    const dragIndex = ref(null)
    const dragOverIndex = ref(null)

    const form = ref({
      category: '',
      title: '',
      content: '',
      images: [],
      is_secret: false
    })
    
    const originalForm = ref({
      category: '',
      title: '',
      content: '',
      images: [],
      is_secret: false
    })

    const isValid = computed(() => {
      return form.value.title.trim() && form.value.content.trim()
    })

    // 기존 데이터 불러오기
    const loadQuestion = async () => {
      if (!props.editIdx) return
      
      const rows = await sql_query(`
        SELECT category, title, content, images, is_secret
        FROM qna_question
        WHERE idx = ${props.editIdx} AND mb_id = '${mb_id}'
      `)
      
      if (rows.length > 0) {
        const data = {
          category: rows[0].category || '',
          title: rows[0].title,
          content: rows[0].content,
          images: rows[0].images ? JSON.parse(rows[0].images) : [],
          is_secret: rows[0].is_secret === 1
        }
        form.value = { ...data, images: [...data.images] }
        originalForm.value = { ...data, images: [...data.images] }
      }
    }

    // 파일 선택
    const triggerFileInput = () => {
      if (uploading.value) return
      fileInput.value.click()
    }

    const handleFileSelect = async (e) => {
      const files = e.target.files
      if (!files.length) return

      uploading.value = true

      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          alert('파일 크기는 5MB 이하만 가능합니다')
          continue
        }

        const formData = new FormData()
        formData.append('idx', props.editIdx || '')
        formData.append('table', 'qna_question')
        formData.append('usage', 'image')
        formData.append('user', mb_id)
        formData.append('file', file)

        try {
          const response = await fetch('/api/upload/image', {
            method: 'POST',
            body: formData,
            mode: 'cors',
            credentials: 'include'
          })
          const data = await response.json()

          if (data.url) {
            let imageUrl = data.url
            if (imageUrl.startsWith('/')) {
              imageUrl = window.location.origin + imageUrl
            }
            form.value.images.push(imageUrl)
          } else {
            alert('이미지 업로드 실패')
          }
        } catch (err) {
          console.error('업로드 오류:', err)
          alert('업로드 중 오류가 발생했습니다')
        }
      }

      uploading.value = false
      e.target.value = ''
    }

    const removeImage = (idx) => {
      form.value.images.splice(idx, 1)
    }
    
    // 이미지 뷰어
    const openViewer = (idx) => {
      viewerIndex.value = idx
      showViewer.value = true
    }
    
    // 드래그앤드롭
    const onDragStart = (e, idx) => {
      dragIndex.value = idx
      e.dataTransfer.effectAllowed = 'move'
    }
    
    const onDragEnd = () => {
      dragIndex.value = null
      dragOverIndex.value = null
    }
    
    const onDragOver = (e, idx) => {
      if (dragIndex.value === null || dragIndex.value === idx) return
      dragOverIndex.value = idx
    }
    
    const onDrop = (idx) => {
      if (dragIndex.value === null || dragIndex.value === idx) return
      
      const images = [...form.value.images]
      const [removed] = images.splice(dragIndex.value, 1)
      images.splice(idx, 0, removed)
      form.value.images = images
      
      dragIndex.value = null
      dragOverIndex.value = null
    }

    // 변경사항 체크
    const hasChanges = () => {
      if (form.value.category !== originalForm.value.category) return true
      if (form.value.title !== originalForm.value.title) return true
      if (form.value.content !== originalForm.value.content) return true
      if (form.value.is_secret !== originalForm.value.is_secret) return true
      if (JSON.stringify(form.value.images) !== JSON.stringify(originalForm.value.images)) return true
      return false
    }

    // 등록/수정
    const submit = async () => {
      if (!isValid.value) return

      const imagesJson = form.value.images.length > 0 
        ? JSON.stringify(form.value.images) 
        : null

      if (isEdit.value) {
        await sql_query(`
          UPDATE qna_question SET
            category = '${form.value.category}',
            title = '${form.value.title.replace(/'/g, "''")}',
            content = '${form.value.content.replace(/'/g, "''")}',
            images = ${imagesJson ? `'${imagesJson.replace(/'/g, "''")}'` : 'NULL'},
            is_secret = ${form.value.is_secret ? 1 : 0}
          WHERE idx = ${props.editIdx} AND mb_id = '${mb_id}'
        `)
      } else {
        await sql_query(`
          INSERT INTO qna_question (mb_id, category, title, content, images, is_secret)
          VALUES (
            '${mb_id}',
            '${form.value.category}',
            '${form.value.title.replace(/'/g, "''")}',
            '${form.value.content.replace(/'/g, "''")}',
            ${imagesJson ? `'${imagesJson.replace(/'/g, "''")}'` : 'NULL'},
            ${form.value.is_secret ? 1 : 0}
          )
        `)
      }

      props.oth_showQnaEdit(false, { refresh: true })
    }

    const close = () => {
      if (hasChanges()) {
        if (!confirm('작성 중인 내용이 있습니다. 정말 나가시겠어요?')) return
      }
      props.oth_showQnaEdit(false)
    }

    onMounted(() => {
      loadQuestion()
    })

    return {
      categories,
      fileInput,
      uploading,
      isEdit,
      form,
      isValid,
      showViewer,
      viewerIndex,
      dragIndex,
      dragOverIndex,
      triggerFileInput,
      handleFileSelect,
      removeImage,
      openViewer,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDrop,
      submit,
      close
    }
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 500px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.header > h2 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0;
}

.header-btn {
  border: none;
  background: none;
  padding: 0;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  cursor: pointer;
}

.header-btn.primary {
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand);
}

.header-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

/* Form */
.form-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group > label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.required {
  color: var(--color-special-warning);
}

/* Category Select */
.category-select {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cat-option {
  padding: 8px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: var(--color-background);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.cat-option.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}

/* Input & Textarea */
.form-group input[type="text"],
.form-group textarea {
  padding: 12px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-background);
  resize: none;
}

.form-group input[type="text"]:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: var(--color-text-third);
}

/* Image Preview */
.image-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.image-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  cursor: grab;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.image-item:active {
  cursor: grabbing;
}

.image-item.dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.image-item.dragover {
  border-color: var(--color-brand);
}

.image-item > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn > i {
  font-size: 12px;
  color: #fff;
}

.img-number {
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: 18px;
  height: 18px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-image-btn {
  width: 80px;
  height: 80px;
  border: 2px dashed var(--color-background-light);
  border-radius: var(--radius-sm);
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-image-btn > i {
  font-size: 24px;
  color: var(--color-text-third);
}

/* Image Upload Empty */
.image-upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  border: 2px dashed var(--color-background-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
}

.image-upload-empty:hover {
  border-color: var(--color-brand);
}

.image-upload-empty > i {
  font-size: 32px;
  color: var(--color-text-third);
}

.image-upload-empty > span {
  font-size: var(--font-size-sm);
  color: var(--color-text-third);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Checkbox */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-background-light);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.checkbox.checked {
  background: var(--color-brand);
  border-color: var(--color-brand);
}

.checkbox > i {
  font-size: 14px;
  color: #fff;
}

.checkbox-label > span:last-child {
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
}

.hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  margin: 0;
}

/* Image Viewer */
.image-viewer {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.viewer-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-close > i {
  font-size: 28px;
  color: #fff;
}

.viewer-content {
  max-width: 90%;
  max-height: 90%;
}

.viewer-content > img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

.viewer-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-arrow.prev { left: 16px; }
.viewer-arrow.next { right: 16px; }

.viewer-arrow > i {
  font-size: 24px;
  color: #fff;
}
</style>